import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignLoadQuery, useCampaignUpdateMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignViewJson from "./campaign-terminals-view.json";

export const CampaignTerminalsViewForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { id } = useParams();

    const formDefinition = campaignViewJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCampaignLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCampaignUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Campaign",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                const route = `${BASE_PATH}/${ResourceName.CAMPAIGNS}`;
                navigate(route);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignTerminalsViewForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
